import { FlipOptions, OffsetOptions, Placement, ShiftOptions } from '@floating-ui/react'
import cn from 'classnames'
import { isNil } from 'lodash'
import React, { useMemo } from 'react'

import styles from './Dropdown.module.scss'
import DropdownMenu from './DropdownMenu'
import MenuHeading from './MenuHeading'
import MenuItem from './MenuItem'
import MenuList from './MenuList'
import Search from './Search'
import { useDropdownMenu } from './lib/useDropdownMenu'

import { formatSizing } from '~lib/styleUtils'
import Arrow from '~shared/ui/Arrow'

interface DropdownProps extends React.HTMLAttributes<HTMLButtonElement> {
  opened?: boolean
  defaultOpened?: boolean
  onOpenedChange?: (opened: boolean) => void
  label: string
  before?: React.ReactNode
  after?: React.ReactNode
  hovered?: boolean
  focused?: boolean
  disabled?: boolean
  width?: number | string
  minHeight?: number | string
  maxHeight?: number | string
  size?: 'sm' | 'md'
  offset?: OffsetOptions
  flip?: FlipOptions
  shift?: ShiftOptions
  placement?: Placement
}

function getDefaultAfter() {
  return <Arrow component="span" direction="bottom" size="sm" />
}

function Dropdown({
  label,
  placeholder,
  before,
  after = getDefaultAfter(),
  children,
  className,
  hovered,
  focused,
  disabled,
  width,
  minHeight,
  maxHeight,
  size = 'md',
  style,
  flip,
  offset = 4,
  shift,
  placement = 'bottom-end',
  opened,
  defaultOpened,
  onOpenedChange,
  ...props
}: DropdownProps) {
  const { refs, getMenuProps, getReferenceProps } = useDropdownMenu({
    offset,
    flip,
    shift,
    opened,
    placement,
    defaultOpened,
    onOpenedChange,
  })

  const computedStyles = useMemo(
    () => ({
      root: {
        ...(!isNil(width) && { width: formatSizing(width) }),
        ...style,
      },
      menu: {
        ...(!isNil(width) && { width: formatSizing(width) }),
        ...(!isNil(minHeight) && { minHeight: formatSizing(minHeight) }),
        ...(!isNil(maxHeight) && { maxHeight: formatSizing(maxHeight) }),
      },
    }),
    [maxHeight, minHeight, width, style]
  )

  return (
    <>
      <button
        ref={refs.setReference}
        type="button"
        disabled={disabled}
        style={computedStyles.root}
        className={cn(
          styles.root,
          size && styles[`size-${size}`],
          focused && styles.focused,
          hovered && styles.hovered,
          disabled && styles.disabled,
          className
        )}
        {...props}
        {...getReferenceProps()}
      >
        {before && <span className={styles.before}>{before}</span>}
        {label && <span className={styles.label}>{label}</span>}
        {!label && <span className={styles.placeholder}>{placeholder}</span>}
        {after && <span className={styles.after}>{after}</span>}
      </button>

      <DropdownMenu ref={refs.setFloating} style={computedStyles.menu} {...getMenuProps()}>
        {children}
      </DropdownMenu>
    </>
  )
}

export default Object.assign(Dropdown, {
  List: MenuList,
  Item: MenuItem,
  Heading: MenuHeading,
  Search,
})
